import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const PhotoModal = ({ photo, photos, onClose, onNavigate }) => {
  const currentIndex = photos.findIndex(p => p.id === photo.id);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(photos[currentIndex - 1]);
      } else if (e.key === 'ArrowRight' && currentIndex < photos.length - 1) {
        onNavigate(photos[currentIndex + 1]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [currentIndex, photos, onClose, onNavigate]);

  const handleDownload = async () => {
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = photo.name || `photo-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to opening in new tab
      const link = document.createElement('a');
      link.href = photo.url;
      link.target = '_blank';
      link.download = photo.name || 'photo.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative max-w-5xl max-h-full w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <img
            src={photo.url}
            alt={photo.name}
            className="max-w-full max-h-[85vh] object-contain mx-auto rounded-lg"
          />

          {/* Controls */}
          <div className="absolute top-4 right-4 flex space-x-2">
            <button
              onClick={handleDownload}
              className="bg-white bg-opacity-10 backdrop-blur-sm text-white p-3 rounded-lg hover:bg-opacity-20 transition-all font-light"
            >
              Download
            </button>
            <button
              onClick={onClose}
              className="bg-white bg-opacity-10 backdrop-blur-sm text-white p-3 rounded-lg hover:bg-opacity-20 transition-all text-xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Navigation */}
          {photos.length > 1 && (
            <>
              {currentIndex > 0 && (
                <button
                  onClick={() => onNavigate(photos[currentIndex - 1])}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-10 backdrop-blur-sm text-white p-3 rounded-lg hover:bg-opacity-20 transition-all text-xl"
                >
                  ←
                </button>
              )}
              {currentIndex < photos.length - 1 && (
                <button
                  onClick={() => onNavigate(photos[currentIndex + 1])}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-10 backdrop-blur-sm text-white p-3 rounded-lg hover:bg-opacity-20 transition-all text-xl"
                >
                  →
                </button>
              )}
            </>
          )}
        </div>

        {/* Photo Info */}
        <div className="absolute bottom-4 left-4 right-4 bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 text-white">
          <div className="flex justify-between items-start">
            <div>
              {photo.name && (
                <h3 className="font-medium mb-1">{photo.name}</h3>
              )}
              {photo.uploaderName && (
                <p className="text-sm opacity-90 font-light">Uploaded by {photo.uploaderName}</p>
              )}
              {photo.uploadedAt && (
                <p className="text-sm opacity-75 font-light">
                  {format(new Date(photo.uploadedAt), 'MMMM d, yyyy • h:mm a')}
                </p>
              )}
            </div>
            {photos.length > 1 && (
              <span className="text-sm opacity-75 font-light">
                {currentIndex + 1} of {photos.length}
              </span>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PhotoModal;